/**
 * **模板文件**：把「设计数据 + 工具设置」存成一个可以下次直接打开的 JSON。
 *
 * ## 为什么要包一层，而不是直接存 `GraphDocument`
 *
 * `graph/serialization.ts` 的 `GraphDocument` 只描述**图**（节点 / 边 / 子图），
 * 那是给运行端用的契约。而「下次打开还要一样」还包括**编辑器自己的一些设置**
 * （调参面板那几个数）。两者混在一份文件里会让运行端的契约变脏 ——
 * 所以这里包一层：
 *
 * ```jsonc
 * {
 *   "kind": "node-print-template",   // 认得出是不是我们的文件
 *   "version": 1,                    // 文件格式版本，与前缀一起做兼容判断
 *   "id": "8f0c…",                   // 文档 UUID（2027 加；可选，老文件没有）
 *   "name": "顺丰面单",               // 文档名（同上）
 *   "document": { …GraphDocument… },  // 设计数据（运行端契约，原样嵌套）
 *   "tool": { "socketSize": …, "rowHeight": …, … }   // 工具设置
 * }
 * ```
 *
 * `document` **原样嵌套、不加工** —— 运行端要的那份必须是同一个形状。
 *
 * ## ⚠️ `id` / `name` 为什么在**外面**这一层，而不是塞进 `document`
 *
 * `document` 是**运行端契约**（`GraphDocument`：节点 / 边 / 子图 / pkg）——
 * 运行端不需要知道"这份东西叫什么、它的 UUID 是多少"。名字与身份是
 * **编辑器 / 文档库**的事，所以它们留在外面这一层，和 `tool` 一起。
 *
 * ★ `id` 就是**文档的唯一 UUID**，它跟着文件走 —— 于是"从文件里读进来一份
 * 文档，库里已经有一份同 UUID 的"这件事认得出（用户要的正是这个：
 * 加载时发现 UUID 重复就弹窗问是否覆盖，见 `graph/documentLibrary.ts`）。
 * 老文件没有这个字段 → 当成新文档（现场生成一个 UUID），不弹窗。
 */

import type { GraphDocument } from '@/graph/serialization'

/** 文件里的标识 —— 打开时先认它，避免把随便一个 JSON 当模板吃进来 */
export const TEMPLATE_KIND = 'node-print-template'

/** 文件格式版本。**加字段不一定要动它**；改了不兼容的读法才动。 */
export const TEMPLATE_VERSION = 1

export interface TemplateFile {
  kind: typeof TEMPLATE_KIND
  version: number
  /** 文档 UUID（可选：2027 之前存的文件没有） */
  id?: string
  /** 文档名（可选：同上） */
  name?: string
  document: GraphDocument
  tool?: ToolSettings
}

/** 工具（调参面板）设置 —— 只放「影响观感、不影响输出结果」的数 */
export interface ToolSettings {
  socketSize: number
  rowHeight: number
  radius: number
  headerHeight: number
  labelRatio: number
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/**
 * 组一份模板文件。
 *
 * `tool` 只在**是完整的一组数**时才带上 —— 缺字段就整个省略，
 * 免得写出一份「一半有效」的设置，下次打开时把面板改成 NaN。
 *
 * `meta`（文档 UUID / 名字）同理只在这两个字段**有值**时写出去：
 * 一个空字符串的 `id` 会让"这份文件是不是库里那一份"变成永远为真或永远为假，
 * 那是比"没有这个字段"更坏的形状。
 */
export function buildTemplateFile(
  document: GraphDocument,
  tool?: Partial<ToolSettings>,
  meta?: { id?: string; name?: string },
): TemplateFile {
  const file: TemplateFile = { kind: TEMPLATE_KIND, version: TEMPLATE_VERSION, document }
  const settings = normalizeToolSettings(tool)
  if (settings) file.tool = settings
  const id = typeof meta?.id === 'string' ? meta.id.trim() : ''
  const name = typeof meta?.name === 'string' ? meta.name.trim() : ''
  if (id) file.id = id
  if (name) file.name = name
  return file
}

/** 只接受 5 个字段**都是有限数**的输入；否则返回 undefined */
export function normalizeToolSettings(input: unknown): ToolSettings | undefined {
  if (!isRecord(input)) return undefined
  const keys: (keyof ToolSettings)[] = [
    'socketSize',
    'rowHeight',
    'radius',
    'headerHeight',
    'labelRatio',
  ]
  const out = {} as ToolSettings
  for (const key of keys) {
    const value = input[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
    out[key] = value
  }
  return out
}

export interface ParseResult {
  ok: boolean
  /** 失败时给用户看的一句话 */
  error?: string
  file?: TemplateFile
}

/**
 * 解析一份模板文件的内容。
 *
 * ⚠️ 这里**只做「是不是我们的文件」的判断**，不做节点级校验 ——
 * 那属于求值 / 校验层（`node/validate.ts`），打开时另跑。
 * 混在这里会让「文件读不进来」和「模板本身有问题」两种错误分不清。
 */
export function parseTemplateFile(text: string): ParseResult {
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch {
    return { ok: false, error: '不是合法的 JSON 文件' }
  }
  if (!isRecord(raw)) return { ok: false, error: '文件内容不是一个对象' }
  if (raw.kind !== TEMPLATE_KIND) {
    return { ok: false, error: '这不是 node-print 的模板文件（缺少 kind 标记）' }
  }
  const version = typeof raw.version === 'number' ? raw.version : 0
  if (version > TEMPLATE_VERSION) {
    return {
      ok: false,
      error: `模板文件版本 ${version} 比当前编辑器新（支持到 ${TEMPLATE_VERSION}），请升级后再打开`,
    }
  }
  if (!isRecord(raw.document)) return { ok: false, error: '模板文件缺少 document' }
  const doc = raw.document
  if (!Array.isArray(doc.nodes) || !Array.isArray(doc.edges)) {
    return { ok: false, error: 'document 里缺少 nodes / edges' }
  }
  return {
    ok: true,
    file: {
      kind: TEMPLATE_KIND,
      version,
      document: doc as unknown as GraphDocument,
      tool: normalizeToolSettings(raw.tool),
      /*
       * ⚠️ `id` / `name` **只在是字符串且非空时**才带上：解析出来一个
       * 空字符串的话，"这份文件是不是库里那一份"这个判断就永远为假 ——
       * 那不是"老文件"，那是"坏文件"，两种都不该被当成有身份。
       */
      ...(typeof raw.id === 'string' && raw.id.trim() !== '' ? { id: raw.id.trim() } : {}),
      ...(typeof raw.name === 'string' && raw.name.trim() !== '' ? { name: raw.name.trim() } : {}),
    },
  }
}

/** 保存时的默认文件名：`模板名-模板ID.json`，去掉文件名里不能用的字符 */
export function templateFileName(name: string, id: string): string {
  const safe = (s: string) => s.replace(/[\\/:*?"<>|\s]+/g, '-').replace(/^-+|-+$/g, '')
  const parts = [safe(name), safe(id)].filter(Boolean)
  return `${parts.join('-') || 'template'}.json`
}
